import type { FC } from "react";
import { useEffect, useState } from "react";
import {
  Button,
  Modal,
  Table,
  TextInput,
  Alert,
  ModalHeader,
  ModalBody,
  ModalFooter,
  TableHead,
  TableHeadCell,
  TableBody,
  TableRow,
  TableCell,
} from "flowbite-react";
import { HiPencilAlt, HiTrash } from "react-icons/hi";
import type { ContractCompany } from "../../services/contract-companies";
import {
  createCompany,
  deleteCompany,
  getContractCompanies,
  updateCompany,
} from "../../services/contract-companies";

interface CompaniesModalProps {
  show: boolean;
  onClose: () => void;
  onUpdate: () => Promise<void>;
}

const CompaniesModal: FC<CompaniesModalProps> = ({
  show,
  onClose,
  onUpdate,
}) => {
  const [companies, setCompanies] = useState<ContractCompany[]>([]);
  const [showInactive, setShowInactive] = useState(true);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newCompany, setNewCompany] = useState({
    company: "",
    active: true,
    color_code: "#007bff",
    color_code2: "#6c757d",
  });
  const [editingCompany, setEditingCompany] = useState<ContractCompany | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const loadCompanies = async () => {
      const data = await getContractCompanies();
      setCompanies(data);
    };
    if (show) {
      loadCompanies();
    }
  }, [show]);

  const validateCompany = (company: { company: string }) => {
    if (!company.company.trim()) {
      setError("Geben Sie einen Firmennamen ein");
      return false;
    }
    return true;
  };

  const handleAddCompany = async () => {
    setError(null);
    setSuccessMessage(null);

    if (!validateCompany(newCompany)) return;

    setIsSubmitting(true);
    try {
      const result = await createCompany({
        company: newCompany.company.trim(),
        active: true,
        color_code: newCompany.color_code,
        color_code2: newCompany.color_code2,
      });

      if (!result.success) {
        throw new Error(result.error || "Failed to create company");
      }

      setSuccessMessage("Firma erfolgreich hinzugefügt");
      await onUpdate();
      const updatedCompanies = await getContractCompanies();
      setCompanies(updatedCompanies);
      setIsAddingNew(false);
      setNewCompany({
        company: "",
        active: true,
        color_code: "#007bff",
        color_code2: "#6c757d",
      });
    } catch (error) {
      console.error("Error creating company:", error);
      setError(
        error instanceof Error ? error.message : "Failed to create company"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditCompany = async () => {
    if (!editingCompany) return;

    setError(null);
    setSuccessMessage(null);

    if (!validateCompany(editingCompany)) return;

    setIsSubmitting(true);
    try {
      const result = await updateCompany(editingCompany.id, {
        company: editingCompany.company.trim(),
        active: editingCompany.active,
        color_code: editingCompany.color_code,
        color_code2: editingCompany.color_code2,
      });

      if (!result.success) {
        throw new Error(result.error || "Fehler beim Aktualisieren der Firma");
      }

      setSuccessMessage("Firma erfolgreich aktualisiert");
      await onUpdate();
      const updatedCompanies = await getContractCompanies();
      setCompanies(updatedCompanies);
      setEditingCompany(null);
    } catch (error) {
      console.error("Error updating company:", error);
      setError(
        error instanceof Error
          ? error.message
          : "Fehler beim Aktualisieren der Firma"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartEdit = (company: ContractCompany) => {
    setEditingCompany(company);
    setError(null);
    setSuccessMessage(null);
  };

  const handleCancelEdit = () => {
    setEditingCompany(null);
    setError(null);
    setSuccessMessage(null);
  };

  const handleDeleteCompany = async (id: number) => {
    try {
      const result = await deleteCompany(id);
      if (result.success) {
        setSuccessMessage("Firma erfolgreich gelöscht");
        await onUpdate();
        const updatedCompanies = await getContractCompanies();
        setCompanies(updatedCompanies);
      } else {
        setError(result.error || "Fehler beim Löschen der Firma");
      }
    } catch (error) {
      console.error("Error deleting company:", error);
      setError(
        error instanceof Error ? error.message : "Fehler beim Löschen der Firma"
      );
    }
  };

  return (
    <Modal size="7xl" onClose={onClose} show={show}>
      <ModalHeader className="border-b border-gray-200 !p-8 dark:border-gray-700">
        <strong>Firmen verwalten</strong>
      </ModalHeader>
      <ModalBody className="max-h-[calc(100vh-200px)] overflow-y-auto p-8">
        {error && (
          <Alert color="failure" className="mb-4">
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert color="success" className="mb-4">
            {successMessage}
          </Alert>
        )}
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            <input
              id="show-inactive"
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 bg-gray-100 text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:ring-offset-gray-800 dark:focus:ring-blue-600"
            />
            <label htmlFor="show-inactive">Inaktive Firmen anzeigen</label>
          </div>
          <Table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
            <TableHead className="bg-gray-100 dark:bg-gray-700">
              <TableHeadCell>Firma</TableHeadCell>
              <TableHeadCell>Farben</TableHeadCell>
              <TableHeadCell>Status</TableHeadCell>
              <TableHeadCell>Aktionen</TableHeadCell>
            </TableHead>
            <TableBody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
              {companies
                .filter((company) => showInactive || company.active)
                .map((company) =>
                  editingCompany?.id === company.id ? (
                    <TableRow
                      key={company.id}
                      className="hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <TableCell className="whitespace-nowrap p-6">
                        <TextInput
                          value={editingCompany.company}
                          onChange={(e) =>
                            setEditingCompany({
                              ...editingCompany,
                              company: e.target.value,
                            })
                          }
                        />
                      </TableCell>
                      <TableCell className="whitespace-nowrap p-6">
                        <div className="flex items-center gap-4">
                          <div>
                            <label
                              htmlFor={`color1-${editingCompany.id}`}
                              className="mb-2 block text-sm"
                            >
                              Farbe 1
                            </label>
                            <input
                              id={`color1-${editingCompany.id}`}
                              type="color"
                              value={editingCompany.color_code}
                              onChange={(e) =>
                                setEditingCompany({
                                  ...editingCompany,
                                  color_code: e.target.value,
                                })
                              }
                              className="h-10 w-20"
                            />
                          </div>
                          <div>
                            <label
                              htmlFor={`color2-${editingCompany.id}`}
                              className="mb-2 block text-sm"
                            >
                              Farbe 2
                            </label>
                            <input
                              id={`color2-${editingCompany.id}`}
                              type="color"
                              value={editingCompany.color_code2}
                              onChange={(e) =>
                                setEditingCompany({
                                  ...editingCompany,
                                  color_code2: e.target.value,
                                })
                              }
                              className="h-10 w-20"
                            />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap p-6">
                        <div
                          className="flex items-center gap-2"
                          role="group"
                          aria-label="Company Status"
                        >
                          <input
                            id={`active-${editingCompany.id}`}
                            type="checkbox"
                            checked={editingCompany.active}
                            onChange={(e) =>
                              setEditingCompany({
                                ...editingCompany,
                                active: e.target.checked,
                              })
                            }
                            className="h-4 w-4 rounded border-gray-300 bg-gray-100 text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:ring-offset-gray-800 dark:focus:ring-blue-600"
                          />
                          <label htmlFor={`active-${editingCompany.id}`}>
                            Aktiv
                          </label>
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap p-6">
                        <div className="flex items-center gap-x-3">
                          <Button
                            color="primary"
                            size="sm"
                            onClick={handleEditCompany}
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? "Speichern..." : "Speichern"}
                          </Button>
                          <Button
                            color="gray"
                            size="sm"
                            onClick={handleCancelEdit}
                          >
                            Abbrechen
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow
                      key={company.id}
                      className="hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <TableCell className="whitespace-nowrap p-6 text-base font-medium text-gray-900 dark:text-white">
                        {company.company}
                      </TableCell>
                      <TableCell className="whitespace-nowrap p-6">
                        <div className="flex items-center gap-4">
                          <div
                            className="h-8 w-8 rounded border border-gray-200"
                            style={{ backgroundColor: company.color_code }}
                          />
                          <div
                            className="h-8 w-8 rounded border border-gray-200"
                            style={{ backgroundColor: company.color_code2 }}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap p-6">
                        <div className="flex items-center gap-x-2">
                          <div
                            className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${
                              company.active
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {company.active ? "Aktiv" : "Inaktiv"}
                          </div>
                          {!company.active && (
                            <span className="text-sm text-gray-500">
                              (Ausgeblendet)
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap p-6">
                        <div className="flex items-center gap-x-3">
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => handleStartEdit(company)}
                          >
                            <HiPencilAlt className="mr-2 text-lg" />
                            Bearbeiten
                          </Button>
                          <Button
                            color="failure"
                            size="sm"
                            onClick={() => handleDeleteCompany(company.id)}
                          >
                            <HiTrash className="mr-2 text-lg" />
                            Löschen
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                )}
              {isAddingNew && (
                <TableRow className="hover:bg-gray-100 dark:hover:bg-gray-700">
                  <TableCell className="whitespace-nowrap p-6">
                    <TextInput
                      value={newCompany.company}
                      onChange={(e) =>
                        setNewCompany({
                          ...newCompany,
                          company: e.target.value,
                        })
                      }
                      placeholder="Firmennamen eingeben"
                    />
                  </TableCell>
                  <TableCell className="whitespace-nowrap p-6">
                    <div className="flex items-center gap-4">
                      <div>
                        <label
                          htmlFor="new-color1"
                          className="mb-2 block text-sm"
                        >
                          Farbe 1
                        </label>
                        <input
                          id="new-color1"
                          type="color"
                          value={newCompany.color_code}
                          onChange={(e) =>
                            setNewCompany({
                              ...newCompany,
                              color_code: e.target.value,
                            })
                          }
                          className="h-10 w-20"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="new-color2"
                          className="mb-2 block text-sm"
                        >
                          Farbe 2
                        </label>
                        <input
                          id="new-color2"
                          type="color"
                          value={newCompany.color_code2}
                          onChange={(e) =>
                            setNewCompany({
                              ...newCompany,
                              color_code2: e.target.value,
                            })
                          }
                          className="h-10 w-20"
                        />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap p-6">
                    <div className="inline-flex rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800">
                      Aktiv
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap p-6">
                    <div className="flex items-center gap-x-3">
                      <Button
                        color="primary"
                        size="sm"
                        onClick={handleAddCompany}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Speichern..." : "Speichern"}
                      </Button>
                      <Button
                        color="gray"
                        size="sm"
                        onClick={() => setIsAddingNew(false)}
                      >
                        Abbrechen
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {!isAddingNew && (
            <div className="flex justify-end">
              <Button color="primary" onClick={() => setIsAddingNew(true)}>
                Firma hinzufügen
              </Button>
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter className="p-8">
        <Button color="gray" onClick={onClose}>
          Schließen
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CompaniesModal;
